import type { CSSProperties, ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { cn } from '@/lib/utils';
import {
  SITSTAT_COLLAPSED_RAIL_WIDTH_PX,
  SITSTAT_RAIL_WIDTH_PERCENT,
} from './sitstat-widget-types';

type RailId = 'left' | 'right';

/**
 * Resize bounds. A bare number is pixels and a string is a percentage, so the
 * floor stays legible on a small screen while the cap scales with the viewport.
 * The map's own minimum keeps two widened rails from crowding it out.
 */
const RAIL_MIN_WIDTH_PX = 260;
const RAIL_MAX_WIDTH = '36%';
const MAP_MIN_WIDTH = '30%';

/** Rail content and the map scroll themselves; the panel must not scroll too. */
const PANEL_STYLE: CSSProperties = { overflow: 'hidden' };

/** The 4px default hit area is thin to grab. */
const HANDLE_CLASS = 'tw:after:w-2';

export function SitStatCollapsedRailStrip({
  side,
  onExpand,
}: {
  side: RailId;
  onExpand: () => void;
}) {
  return (
    <aside
      className={cn(
        'tw:flex tw:h-full tw:shrink-0 tw:flex-col tw:items-center tw:border-border tw:bg-background tw:py-2 tw:shadow-sm',
        side === 'left' ? 'tw:border-r' : 'tw:border-l'
      )}
      style={{ width: SITSTAT_COLLAPSED_RAIL_WIDTH_PX }}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="tw:h-8 tw:w-8"
        aria-label={`Expand ${side} SitStat rail`}
        onClick={onExpand}
      >
        {side === 'left' ? (
          <ChevronRight className="tw:h-4 tw:w-4" />
        ) : (
          <ChevronLeft className="tw:h-4 tw:w-4" />
        )}
      </Button>
      <span
        className="tw:mt-2 tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground"
        style={{ writingMode: 'vertical-rl' }}
      >
        SitStat
      </span>
    </aside>
  );
}

type SitStatRailsShellProps = {
  mapSlot: ReactNode;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  onLeftCollapsedChange: (collapsed: boolean) => void;
  onRightCollapsedChange: (collapsed: boolean) => void;
  onExitSitStat?: () => void;
  leftTitle?: ReactNode;
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  leftContent: ReactNode;
  rightContent: ReactNode;
  className?: string;
};

export function SitStatRailsShell({
  mapSlot,
  leftCollapsed,
  rightCollapsed,
  onLeftCollapsedChange,
  onRightCollapsedChange,
  onExitSitStat,
  leftTitle,
  leftActions,
  rightActions,
  leftContent,
  rightContent,
  className,
}: SitStatRailsShellProps) {
  const renderRail = (side: RailId) => {
    const setCollapsed =
      side === 'left' ? onLeftCollapsedChange : onRightCollapsedChange;
    const actions = side === 'left' ? leftActions : rightActions;
    const content = side === 'left' ? leftContent : rightContent;

    return (
      <aside
        className={cn(
          'tw:flex tw:h-full tw:min-h-0 tw:w-full tw:min-w-0 tw:flex-col tw:bg-background tw:shadow-sm',
          side === 'left' ? 'tw:border-r' : 'tw:border-l'
        )}
      >
        <div className="tw:flex tw:h-11 tw:shrink-0 tw:items-center tw:gap-2 tw:border-b tw:px-2">
          {side === 'left' && onExitSitStat ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="tw:h-7 tw:shrink-0 tw:gap-1 tw:px-2 tw:text-[11px]"
              aria-label="Exit SitStat"
              onClick={onExitSitStat}
            >
              <X className="tw:h-3.5 tw:w-3.5" />
              Exit SitStat
            </Button>
          ) : null}
          {side === 'left' && leftTitle ? (
            <div className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-xs tw:font-semibold">
              {leftTitle}
            </div>
          ) : (
            <div className="tw:min-w-0 tw:flex-1" />
          )}
          <div className="tw:ml-auto tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            {actions}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="tw:h-7 tw:w-7 tw:shrink-0"
              aria-label={`Collapse ${side} SitStat rail`}
              onClick={() => setCollapsed(true)}
            >
              {side === 'left' ? (
                <ChevronLeft className="tw:h-4 tw:w-4" />
              ) : (
                <ChevronRight className="tw:h-4 tw:w-4" />
              )}
            </Button>
          </div>
        </div>
        {content}
      </aside>
    );
  };

  return (
    <div
      className={cn(
        'tw:flex tw:h-full tw:min-h-0 tw:w-full tw:overflow-hidden',
        className
      )}
    >
      {/* A collapsed rail is a fixed-width strip, which does not express
          cleanly as a panel percentage, so it sits outside the group. */}
      {leftCollapsed ? (
        <SitStatCollapsedRailStrip
          side="left"
          onExpand={() => onLeftCollapsedChange(false)}
        />
      ) : null}

      <ResizablePanelGroup
        orientation="horizontal"
        className="tw:min-w-0 tw:flex-1"
      >
        {!leftCollapsed && (
          <>
            <ResizablePanel
              id="sitstat-rail-left"
              defaultSize={`${SITSTAT_RAIL_WIDTH_PERCENT}%`}
              minSize={RAIL_MIN_WIDTH_PX}
              maxSize={RAIL_MAX_WIDTH}
              className="tw:min-w-0"
              style={PANEL_STYLE}
            >
              {renderRail('left')}
            </ResizablePanel>
            <ResizableHandle withHandle className={HANDLE_CLASS} />
          </>
        )}

        <ResizablePanel
          id="sitstat-map"
          minSize={MAP_MIN_WIDTH}
          className="tw:min-w-0"
          style={PANEL_STYLE}
        >
          <div className="tw:relative tw:h-full tw:w-full tw:overflow-hidden">
            {mapSlot}
          </div>
        </ResizablePanel>

        {!rightCollapsed && (
          <>
            <ResizableHandle withHandle className={HANDLE_CLASS} />
            <ResizablePanel
              id="sitstat-rail-right"
              defaultSize={`${SITSTAT_RAIL_WIDTH_PERCENT}%`}
              minSize={RAIL_MIN_WIDTH_PX}
              maxSize={RAIL_MAX_WIDTH}
              className="tw:min-w-0"
              style={PANEL_STYLE}
            >
              {renderRail('right')}
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>

      {rightCollapsed ? (
        <SitStatCollapsedRailStrip
          side="right"
          onExpand={() => onRightCollapsedChange(false)}
        />
      ) : null}
    </div>
  );
}
